import styled from 'styled-components';

import type { ExtendedTheme } from '@theme/theme';
import { customScrollStyle } from '@theme/GlobalStyle';

const NavCurrentFileColor = ({ theme }: { theme: ExtendedTheme }) => theme.palette.components.scrollBg;

export const NavActiveTitle = styled.p`
  position: relative;
  text-transform: uppercase;
  letter-spacing: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  padding: 24px 36px 22px 20px;
  box-shadow: ${({ theme }: { theme: ExtendedTheme }) => theme.palette.components.blocksShadow};
  background: ${({ theme }: { theme: ExtendedTheme }) => theme.palette.components.navBg};
  font-size: 10px;
  color: ${({ theme }: { theme: ExtendedTheme }) => theme.palette.text.primary};
  font-weight: ${({ theme }: { theme: ExtendedTheme }) => theme.typography.fontWeightBold};

  &.collapsed {
    &:after {
      transform: rotate(180deg);
    }
  }

  &:after {
    content: '';
    position: absolute;
    top: 50%;
    margin-top: -3px;
    right: 16px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 6px 6px 0 6px;
    border-color: ${({ theme }: { theme: ExtendedTheme }) => theme.palette.text.secondary} transparent transparent
      transparent;
  }
`;

export const NavWrapper = styled.div`
  overflow-y: scroll;

  ${({ theme }: { theme: ExtendedTheme }) => theme.breakpoints.up('lg')} {
    position: sticky;
    top: 0;
  }

  ${customScrollStyle};

  &::-webkit-scrollbar-thumb {
    border: 4px solid ${({ theme }: { theme: ExtendedTheme }) => theme.palette.background.paper};

    ${({ theme }: { theme: ExtendedTheme }) => theme.breakpoints.down('lg')} {
      border: 4px solid ${({ theme }: { theme: ExtendedTheme }) => theme.palette.components.navBg};
    }
  }

  ${({ theme }: { theme: ExtendedTheme }) => theme.breakpoints.down('lg')} {
    overflow: hidden;
    padding: 0;
    width: 100%;
    box-shadow: ${({ theme }: { theme: ExtendedTheme }) => theme.palette.components.blocksShadow};
    background: ${({ theme }: { theme: ExtendedTheme }) => theme.palette.components.navBg};
    z-index: ${({ theme }: { theme: ExtendedTheme }) => theme.zIndex.appBar};
  }
`;

export const NavScroll = styled.div<{ $scrolled: boolean }>`
  padding: 20px 0 18px 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: ${({ $scrolled }) => ($scrolled ? '100vh' : 'calc(100vh - 72px)')};
  transition: height 0.2s ease-in;

  ${({ theme }: { theme: ExtendedTheme }) => theme.breakpoints.down('lg')} {
    overflow: auto;

    &::-webkit-scrollbar {
      width: 16px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${({ theme }: { theme: ExtendedTheme }) => theme.palette.components.scrollBg};
      border-radius: 10px;
      border: 4px solid ${({ theme }: { theme: ExtendedTheme }) => theme.palette.components.navBg};
    }
  }
`;

export const NavPanel = styled.div`
  margin-bottom: 18px;

  ${({ theme }: { theme: ExtendedTheme }) => theme.breakpoints.down('lg')} {
    height: auto;
  }

  ul {
    &:first-child {
      ${({ theme }: { theme: ExtendedTheme }) => theme.breakpoints.down('lg')} {
        padding-bottom: 15px;
      }

      > li {
        &:first-child {
          ${({ theme }: { theme: ExtendedTheme }) => theme.breakpoints.down('lg')} {
            margin: 0 0 15px 0;
            padding: 9px 20px;
            border-bottom: 1px solid ${({ theme }: { theme: ExtendedTheme }) => theme.palette.components.navBorder};
          }

          div {
            background: none;
          }

          p {
            ${({ theme }: { theme: ExtendedTheme }) => theme.breakpoints.down('lg')} {
              padding-left: 0;
              margin-right: 0;
            }
          }

          a {
            font-weight: ${({ theme }: { theme: ExtendedTheme }) => theme.typography.fontWeightBold};
          }
        }
      }
    }
  }
`;

export const NavLinkWrapper = styled.p`
  position: relative;
  margin: 0;
  padding-left: 32px;
  cursor: pointer;

  &.prevent-redirect {
    pointer-events: none;
    cursor: pointer;
  }

  a {
    display: block;
    padding: 12px 10px 12px 0;
    text-decoration: none;
    color: ${({ theme }: { theme: ExtendedTheme }) => theme.palette.text.secondary};

    ${({ theme }: { theme: ExtendedTheme }) => theme.breakpoints.down('lg')} {
      font-size: 12px;
    }

    li & {
      li & {
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
  }
`;

export const NavArrowWrapper = styled.span`
  display: ${(props: { isOpened: boolean; isDirectory: boolean }) => (props.isDirectory ? 'inline' : 'none')};
  position: absolute;
  width: 16px;
  height: 16px;
  top: 14px;
  left: 10px;
  cursor: pointer;
  transition: transform 0.3s;
  background-image: url("data:image/svg+xml, %3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M6.23578 3.52838L10.7072 7.99979L6.23578 12.4712L5.29297 11.5284L8.82156 7.99979L5.29297 4.47119L6.23578 3.52838Z' fill='${({
    theme,
  }: {
    theme: ExtendedTheme;
  }) => theme.palette.text.secondary}' /%3E%3C/svg%3E");
  opacity: 0.72;
  transform: ${(props: { isOpened: boolean; isDirectory: boolean }) =>
    props.isOpened ? 'rotate(90deg)' : 'rotate(0)'};
`;

export const ActiveNavItem = styled.div`
  cursor: pointer;
  background: ${(props: { isActive: boolean }) => (props.isActive ? NavCurrentFileColor : 'transparent')};
  border-radius: ${(props: { isActive: boolean }) => (props.isActive ? '4px' : '0')};

  ${({ theme }: { theme: ExtendedTheme }) => theme.breakpoints.down('lg')} {
    margin: 0 10px;
  }
`;

export const NavMenuWrapper = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
`;

export const ThemeSwitcherWrapper = styled.div`
  margin-top: auto;
`;
